import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { recalculateWellnessRequest } from '../requests/user';
import type StoreService from 'ember-data/store';
// @ts-expect-error: this doesn't exist yet
import type SessionContextService from './session-context.ts';

export default class WellnessTrainRecalculatorService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked lastRecalculationTime?: Date;
  @tracked lastCooldownResetTime?: Date;

  /**
   * Minimum time between recalculations to prevent excessive API calls (5 minutes)
   */
  private readonly RECALCULATION_COOLDOWN_MS = 5 * 60 * 1000;

  /**
   * Check if enough time has passed since last recalculation
   * Also validates that sessionContext is ready
   */
  get canRecalculate() {
    // Must have session context loaded and user ID
    if (!this.sessionContext.isLoaded || !this.sessionContext.user?.id) {
      return false;
    }

    if (!this.lastRecalculationTime) return true;
    const elapsed = Date.now() - this.lastRecalculationTime.getTime();
    return elapsed > this.RECALCULATION_COOLDOWN_MS;
  }

  /**
   * Trigger wellness train recalculation in the background.
   * This is a "fire and forget" operation - failures are logged but not surfaced to user.
   * Use `recalculate.isRunning` to check if recalculation is in progress.
   *
   * @param options.force - Skip cooldown check and force recalculation
   * @param options.userId - Explicitly provide user ID (for admin recalculating on behalf of user)
   * @returns Promise that resolves when recalculation request completes
   */
  recalculate = dropTask(async (options: { force?: boolean; userId?: string } = {}) => {
    const { force = false, userId: explicitUserId } = options;

    // Use explicit userId if provided (admin on behalf of user), otherwise use sessionContext
    let userId = explicitUserId;

    if (!userId) {
      // Validate session context is ready when no explicit userId
      if (!this.sessionContext?.isLoaded) {
        return;
      }
      userId = this.sessionContext.user?.id;
    }

    if (!userId) {
      console.warn('[wellness-train-recalculator] Cannot recalculate - no user ID available');
      return;
    }

    // Check cooldown unless forced or explicit userId provided (admin requests skip cooldown)
    if (!force && !explicitUserId && !this.canRecalculate) {
      console.debug(
        '[wellness-train-recalculator] Skipping recalculation - within cooldown period'
      );
      return;
    }

    try {
      const source = explicitUserId ? 'admin' : 'user';
      console.debug('[wellness-train-recalculator] Starting wellness train recalculation', {
        userId,
        source,
      });

      // Trigger recalculation
      await this.store.request(recalculateWellnessRequest(userId));

      // Update last recalculation time
      this.lastRecalculationTime = new Date();
      console.debug('[wellness-train-recalculator] Recalculation complete');
    } catch (error) {
      // Silent failure - log but don't surface to user
      // Wellness train recalculation is a nice-to-have, not critical for app function
      console.error('[wellness-train-recalculator] Recalculation failed', error);
    }
  });

  /**
   * Reset the cooldown timer (useful for testing or forced recalculations)
   * Note: This is primarily for testing - don't use in production
   */
  resetCooldown() {
    this.lastRecalculationTime = undefined;
    this.lastCooldownResetTime = new Date();
  }
}

declare module '@ember/service' {
  interface Registry {
    'wellness-train-recalculator': WellnessTrainRecalculatorService;
  }
}
